import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { matchPath, useLocation } from "react-router-dom";

import Alert from "veneer/Alert";
import AutocompleteSelectInput, {
  type AutocompleteSelectInputProps,
  createFilterOptions,
} from "veneer/AutocompleteSelectInput";
import Drawer from "veneer/Drawer";
import ListItem from "veneer/ListItem";
import { showSuccess } from "veneer/SnackBar";
import Stack from "veneer/Stack";
import TextInput from "veneer/TextInput";

import { useAppDispatch } from "cortex/ui/src/hooks";
import { createPromiseApiAction } from "cortex/ui/src/redux-store/api-sagas";
import { NEW_DEVICE } from "cortex/ui/src/redux-store/devices/actions";
import SegmentedControl, {
  SegmentedControlButton,
} from "cortex/ui/src/ui/segmented-control";
import { Option } from "cortex/ui/src/ui/table/types";

type TagsAutocompleteProps = AutocompleteSelectInputProps<
  Option,
  true,
  false,
  true
>;

const filter = createFilterOptions<Option>();

const filterOptions: TagsAutocompleteProps["filterOptions"] = (
  options,
  params,
) => {
  const filtered = filter(options, params);
  const raw = params.inputValue.trim();
  const exists = options.some(
    (o) => o.label.toLowerCase() === raw.toLowerCase(),
  );

  if (raw && !exists) {
    filtered.push({ label: raw, value: raw });
  }

  return filtered;
};

export interface RegisterSingleDeviceDrawerProps {
  networkOptions: ReadonlyArray<Option>;
  onCancel: () => void;
  open: boolean;
  tagsOptions: ReadonlyArray<Option>;
}

type FormValues = {
  deviceName: string;
  isAgent: boolean;
  location: string;
  networkId: number | null;
  partnerVenueId: number | null;
  registrationCode: string;
  tags: ReadonlyArray<string>;
};

type ErrorState<T> = Partial<Record<keyof T, string>>;

const DEFAULT_FORM: FormValues = {
  deviceName: "",
  isAgent: false,
  location: "",
  networkId: null,
  partnerVenueId: null,
  registrationCode: "",
  tags: [],
};

function getNetworkIdFromLocation(location: ReturnType<typeof useLocation>) {
  const match = matchPath<{ id: string }>(location.pathname, {
    path: "/networks/:id",
    exact: true,
    strict: false,
  });
  if (!match) {
    return undefined;
  }
  const networkId = Number(match.params.id);
  return Number.isNaN(networkId) ? undefined : networkId;
}

export default function RegisterSingleDeviceDrawer({
  networkOptions = [],
  onCancel,
  open,
  tagsOptions = [],
}: RegisterSingleDeviceDrawerProps) {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const isMountedRef = useRef(true);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState<FormValues>(DEFAULT_FORM);
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<ErrorState<FormValues>>({});
  const [tagInputValue, setTagInputValue] = useState("");
  const networkIdFromLocation = getNetworkIdFromLocation(location);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const selectedTagOptions: Option[] = (form.tags ?? []).map((v) => {
    const match = tagsOptions.find((o) => String(o.value) === String(v));
    return match ?? { label: v, value: v };
  });
  const selectedNetworkOption = useMemo(
    () =>
      networkOptions.find(
        (opt) => String(opt.value) === String(form.networkId),
      ) ?? null,
    [form.networkId, networkOptions],
  );

  const registerHelperText = form.isAgent
    ? "Enter the 6 letter registration code stored on your device or your device MAC address."
    : "Enter the 6 letter registration code displayed on your device or your device MAC address.";

  useEffect(() => {
    if (!open) {
      setSubmitted(false);
      setForm(DEFAULT_FORM);
    }
  }, [open]);

  useEffect(() => {
    if (networkIdFromLocation) {
      setForm((prevForm) => ({
        ...prevForm,
        networkId: networkIdFromLocation,
      }));
    }
  }, [networkIdFromLocation]);

  const handleTagsChange = useCallback<
    NonNullable<TagsAutocompleteProps["onChange"]>
  >((_, newValue) => {
    const normalized: Option[] = (newValue ?? []).map((item) =>
      typeof item === "string" ? { label: item, value: item } : item,
    );

    setForm((prevForm) => ({
      ...prevForm,
      tags: normalized.map((opt) => String(opt.label)),
    }));
    setTagInputValue("");
  }, []);

  function validateForm(values: FormValues) {
    const validations: ErrorState<FormValues> = {};

    if (!values.registrationCode || values.registrationCode.trim() === "") {
      validations.registrationCode = "Registration code is required.";
    }

    if (!values.networkId) {
      validations.networkId = "Network selection is required.";
    }

    if (!values.deviceName || values.deviceName.trim() === "") {
      validations.deviceName = "Device name is required.";
    }

    setErrors(validations);
    return validations;
  }

  const handleRegisterDevice = useCallback(async () => {
    const validation = validateForm(form);
    if (Object.keys(validation).length > 0) return;
    setSubmitted(true);
    setError(null);

    try {
      const {
        deviceName,
        isAgent,
        location,
        networkId,
        partnerVenueId,
        registrationCode,
        tags,
      } = form;

      await createPromiseApiAction(dispatch, NEW_DEVICE, [
        registrationCode,
        deviceName,
        networkId,
        tags,
        partnerVenueId,
        location,
        isAgent,
      ]);

      showSuccess("Your device is registered now.");
      onCancel();
    } catch (_err: any) {
      if (isMountedRef.current) {
        setError(
          _err?.data?.message ||
            "An unexpected error occurred while registering the device. Please try again.",
        );
        setSubmitted(false);
      }
    }
  }, [dispatch, form, onCancel]);

  const clearError = useCallback((key: keyof FormValues) => {
    setErrors((prev) => {
      const { [key]: _omit, ...rest } = prev;
      return rest;
    });
  }, []);

  return (
    <Drawer
      commitLabel="Register"
      commitLoading={submitted}
      onCancel={onCancel}
      onClose={onCancel}
      onCommit={handleRegisterDevice}
      open={open}
      title="Register Device"
    >
      <Stack gap={2}>
        {error && <Alert severity="error">{error}</Alert>}
        <SegmentedControl
          label="Registration Type"
          onChange={(value?: string) => {
            setForm({ ...form, isAgent: value === "true" });
          }}
        >
          <SegmentedControlButton
            selected={form.isAgent === false}
            value="false"
          >
            Cortex Player
          </SegmentedControlButton>
          <SegmentedControlButton selected={form.isAgent === true} value="true">
            Agent
          </SegmentedControlButton>
        </SegmentedControl>
        <TextInput
          error={Boolean(errors.registrationCode)}
          fullWidth
          helperText={errors.registrationCode || registerHelperText}
          label="Registration Code"
          onChange={(value: string | null) => {
            setForm({ ...form, registrationCode: value ?? "" });
            if (errors.registrationCode) clearError("registrationCode");
          }}
          size="medium"
          value={form.registrationCode}
        />
        <AutocompleteSelectInput
          disableClearable
          inputProps={{
            error: Boolean(errors.networkId),
            helperText:
              errors.networkId ||
              "Please select the network this device will be part of.",
            label: "Network",
          }}
          isOptionEqualToValue={(opt, value) =>
            String(opt.value) === String(value.value)
          }
          onChange={(_e: any, opt: Option | null) => {
            setForm({
              ...form,
              networkId:
                opt == null
                  ? null
                  : typeof opt.value === "number"
                    ? opt.value
                    : Number(opt.value),
            });
            if (errors.networkId) clearError("networkId");
          }}
          options={networkOptions}
          value={selectedNetworkOption as Option}
        />
        <TextInput
          error={Boolean(errors.deviceName)}
          fullWidth
          helperText={
            errors.deviceName || "Name your device for future reference."
          }
          label="Device Name"
          onChange={(value: string | null) => {
            setForm({ ...form, deviceName: value ?? "" });
            if (errors.deviceName) clearError("deviceName");
          }}
          size="medium"
          value={form.deviceName}
        />
        <AutocompleteSelectInput
          filterOptions={filterOptions}
          forcePopupIcon
          freeSolo
          getOptionLabel={(opts: Option | string) =>
            typeof opts === "string" ? opts : opts.label
          }
          inputProps={{
            helperText: `Type one tag at a time and hit Enter. Tags are case-insensitive and have to start with alphanumeric characters. Tags can only contain alphanumeric characters, "-", "_" or a space.`,
            label: "Tags (Optional)",
          }}
          inputValue={tagInputValue}
          isOptionEqualToValue={(
            opts: Option | string,
            value: Option | string,
          ) =>
            (typeof opts === "string" ? opts : opts.value) ===
            (typeof value === "string" ? value : value.value)
          }
          multiple
          onChange={handleTagsChange}
          onInputChange={(_: any, newInput: string) => {
            setTagInputValue(newInput);
          }}
          options={tagsOptions}
          renderOption={(props: any, opt: Option) => (
            <ListItem {...props} key={String((opt as Option).value)}>
              {(opt as Option).label}
            </ListItem>
          )}
          value={selectedTagOptions}
        />
      </Stack>
    </Drawer>
  );
}
